import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/api";
import { AppShell } from "@/components/AppShell";
import { DashboardSensorTile } from "@/components/DashboardSensorTile";
import { SendSmsWidget } from "@/components/SendSmsWidget";
import { ActiveAlertsBanner } from "@/components/ActiveAlertsBanner";
import { useAuth } from "@/auth/AuthProvider";

const POLL_MS = 10000;

export default function Dashboard() {
  const { user } = useAuth();
  const [sensors, setSensors] = useState([]);
  const [error, setError] = useState("");
  const [lastFetched, setLastFetched] = useState(null);

  const fetchSensors = useCallback(async () => {
    try {
      const { data } = await api.get("/sensors");
      setSensors(data);
      setLastFetched(new Date());
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    fetchSensors();
    const t = setInterval(fetchSensors, POLL_MS);
    return () => clearInterval(t);
  }, [fetchSensors]);

  return (
    <AppShell>
      <ActiveAlertsBanner sensors={sensors} onAck={fetchSensors} />

      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
            Live status
          </div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">
            Walk-ins
          </h1>
        </div>
        <div
          className="text-[10px] uppercase tracking-widest text-zinc-500"
          data-testid="dashboard-last-refreshed"
        >
          {lastFetched
            ? `Refreshed ${lastFetched.toLocaleTimeString()} · auto ${POLL_MS / 1000}s`
            : "Loading…"}
        </div>
      </div>

      {error && (
        <div
          data-testid="dashboard-error"
          className="mb-6 text-sm text-red-400 border-l-2 border-red-500 pl-3"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="sensor-grid">
        {sensors.map((s) => (
          <DashboardSensorTile key={s.id} sensor={s} />
        ))}
      </div>

      {user?.role === "admin" && (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <SendSmsWidget />
        </div>
      )}

      {user?.role === "admin" && (
        <div
          className="mt-10 pt-6 border-t border-zinc-800 flex items-center justify-between"
          data-testid="dashboard-footer"
        >
          <div className="text-xs text-zinc-500">
            On-prem polling agent for Windows — install on a machine on the
            same LAN as the walk-in WD15s.
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/agent/download`}
            data-testid="download-agent-link"
            className="inline-flex items-center gap-2 rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 px-4 py-2 uppercase tracking-widest text-xs font-bold"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Windows agent
          </a>
        </div>
      )}
    </AppShell>
  );
}
